<template>
	<div class="py-6 first:pt-0 last:pb-8">
		<div class="flex items-center justify-between space-x-1.5">
			<div class="flex-grow">
				<input type="text" v-model="recording.title" class="bg-transparent border-transparent px-0 py-0 w-full truncate">
			</div>
			<div>
				<div v-if="recording.status == 'recording'" class="relative w-3 h-3 rounded-full">
					<div class="absolute w-3 h-3 rounded-full bg-red-500"></div>
					<div class="absolute w-3 h-3 rounded-full bg-red-500 animate-ping"></div>
				</div>
				<svg v-if="recording.status == 'uploading'" class="animate-spin h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
					<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
					<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
				</svg>

				<svg v-if="recording.status == 'finished'" class="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
			</div>
			<div class="py-1.5 px-2.5 bg-gray-800 text-gray-400 text-xs rounded-full tabular-nums">{{ recording.duration.string }}</div>

			<Menu as="div">
				<div>
					<MenuButton v-if="recording.status == 'finished'" class="focus:outline-none focus:ring-0 focus:ring-regal-blue-700 focus:ring-offset-0">
						<div class="w-8 h-8 rounded-full flex items-center justify-center group hover:bg-gray-200 cursor-pointer">
							<svg class="w-5 h-5 text-gray-700 group-hover:text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"></path></svg>
						</div>
					</MenuButton>
					<div v-else class="w-8 h-8 rounded-full flex items-center justify-center cursor-not-allowed">
						<svg class="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"></path></svg>
					</div>
				</div>

				<transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
					<MenuItems class="absolute right-4 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
						<div class="py-1">
							<MenuItem v-slot="{ active, close }">
								<Link :href="route('recordings.show', recording.uuid)" preserve-state @click="close" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2.5 text-sm flex space-x-2.5 font-medium']">
								<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243 4.243 3 3 0 004.243-4.243zm0-5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243z"></path></svg>
								<div>Edit</div>
							</Link>
						</MenuItem>
					</div>
					<div class="py-1">
						<MenuItem v-slot="{ active, close }">
							<Link :href="route('episodes.create', { recording: recording.uuid })" preserve-state @click="close" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2.5 text-sm flex space-x-2.5 font-medium']">
							<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path></svg>
							<div>Publish episode</div>
						</Link>
					</MenuItem>
				</div>
				<div class="py-1">
					<MenuItem v-slot="{ active, close }">
						<div @click="deleteRecording" :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-4 py-2.5 text-sm flex space-x-2.5 font-medium w-full cursor-pointer']">
							<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
							<div>Delete</div>
						</div>
				</MenuItem>
			</div>
		</MenuItems>
	</transition>
</Menu>

		</div>
		<div class="flex space-x-2 items-center mt-1">
			<div>
				<template v-for="participant in recording.participants">
					<div class="w-8 h-8 rounded-full bg-gray-300 bg-center bg-cover flex items-center justify-center text-xs text-gray-600 font-medium" :style="'background-image: url(' + participant.avatar + ')'">
						<span v-if="!participant.avatar">{{ participant?.name?.charAt(0).toUpperCase() }}{{ participant?.name?.charAt(1) }}</span>
					</div>
				</template>
			</div>
			<div>
				<div></div>
				<div class="text-xs text-gray-700">{{ formatTimestamp(recording.startedAt) }}</div>
			</div>
		</div>
		<audio v-if="recording.previewUrl" :src="recording.previewUrl" controls class="w-full mt-2" />
	</div>
</template>

<script setup>
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { defineProps, defineEmits } from 'vue'
import axios from 'axios'

const props = defineProps(['user', 'recording'])
const emit = defineEmits(['delete'])

const formatTimestamp = (timestamp) => {
	const date = new Date(timestamp * 1000)
	const string = date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'})
	return string.replace(',', ' ')
}

const deleteRecording = () => {
	props.recording.status = 'uploading'
	axios.delete(route('recordings.destroy', {recording: props.recording.uuid}))
		.then(res => {
			emit('delete', props.recording)
		})
}
</script>